import { useMemo, useState } from 'react'

interface ScopeOption {
  key: string
  label: string
}

export interface AccordionRole {
  role: string
  exclusive: ScopeOption[]
  full_role: ScopeOption[]
}

const ROLE_TITLES: Record<string, string> = {
  superadmin: 'Superadmin',
  director: 'Direktor',
  admin: 'Admin',
  hr: 'HR',
  teacher: 'Lehrperson',
}

const ROLE_SHORT: Record<string, string> = {
  superadmin: 'SA',
  director: 'Dir.',
  admin: 'Admin',
  hr: 'HR',
  teacher: 'LP',
}

const ROLE_BLURB: Record<string, string> = {
  superadmin: 'Schuleinstellungen, Import, Audit …',
  admin: 'Tagesbetrieb, buchen, im Auftrag',
  director: 'Entscheide, Dokumente, manuell zuweisen',
  hr: 'Reports, Statistik, HR-Daten',
  teacher: 'Eigene Anfrage, annehmen, ablehnen',
}

const ROLE_BADGE: Record<string, string> = {
  superadmin: 'bg-purple-100 text-purple-800',
  admin: 'bg-blue-100 text-blue-700',
  director: 'bg-indigo-100 text-indigo-800',
  hr: 'bg-amber-100 text-amber-800',
  teacher: 'bg-green-100 text-green-800',
}

function resourceOf(perm: string): string {
  return perm.split('.')[0] || perm
}

function isRead(perm: string): boolean {
  if (perm === 'radar.view' || perm === 'audit_log.view' || perm === 'admin.dashboard.view' || perm === 'superadmin.dashboard.view') return true
  if (perm === 'hr.report.view' || perm === 'hr.statistics.view' || perm === 'director.decisions.view') return true
  return perm.endsWith('.read') || perm.endsWith('.list')
}

function overlapLabel(otherRoles: string[]): string | null {
  if (otherRoles.length === 0) return null
  if (otherRoles.length >= 3) return `auch ${otherRoles.length} Rollen`
  return `auch ${otherRoles.map((r) => ROLE_SHORT[r] || r).join(', ')}`
}

interface Props {
  accordions: AccordionRole[]
  scopes: string[]
  onChange: (scopes: string[]) => void
  disabled?: boolean
}

export function EngineerScopeAccordion({ accordions, scopes, onChange, disabled = false }: Props) {
  const selected = new Set(scopes)
  const [openRoles, setOpenRoles] = useState<Record<string, boolean>>({ superadmin: true })

  const rolesByKey = useMemo(() => {
    const map = new Map<string, string[]>()
    for (const acc of accordions) {
      for (const opt of acc.full_role) {
        const list = map.get(opt.key) ?? []
        if (!list.includes(acc.role)) list.push(acc.role)
        map.set(opt.key, list)
      }
    }
    return map
  }, [accordions])

  const apply = (next: string[]) => onChange(Array.from(new Set(next)))

  const toggle = (key: string) => {
    if (disabled) return
    if (selected.has(key)) {
      let next = scopes.filter((s) => s !== key)
      if (isRead(key)) {
        const res = resourceOf(key)
        next = next.filter((s) => !(resourceOf(s) === res && !isRead(s)))
      }
      apply(next)
      return
    }
    const next = [...scopes, key]
    if (!isRead(key)) {
      const res = resourceOf(key)
      const accReads = accordions.flatMap((a) => [...a.exclusive, ...a.full_role]).filter((o) => resourceOf(o.key) === res && isRead(o.key))
      for (const r of accReads) {
        if (!next.includes(r.key)) next.push(r.key)
      }
    }
    apply(next)
  }

  const setRoleComplete = (acc: AccordionRole) => {
    apply([...scopes, ...acc.full_role.map((o) => o.key)])
  }

  const clearRole = (acc: AccordionRole) => {
    const drop = new Set(acc.full_role.map((o) => o.key))
    const droppedReads = acc.full_role.filter((o) => selected.has(o.key) && isRead(o.key))
    let next = scopes.filter((s) => !drop.has(s))
    for (const r of droppedReads) {
      const res = resourceOf(r.key)
      next = next.filter((s) => !(resourceOf(s) === res && !isRead(s)))
    }
    apply(next)
  }

  return (
    <div>
      <p className="text-xs text-gray-500 mb-2.5 leading-snug">
        Haken in einer Rolle gilt global. Ein Scope, das in mehreren Rollen vorkommt, wird überall gleich gesetzt.
      </p>
      {accordions.map((acc) => {
        const fullKeys = acc.full_role.map((o) => o.key)
        const checkedFull = fullKeys.filter((k) => selected.has(k)).length
        const complete = fullKeys.length > 0 && fullKeys.every((k) => selected.has(k))
        const open = openRoles[acc.role] ?? false
        const title = ROLE_TITLES[acc.role] || acc.role
        return (
          <div key={acc.role} className="border border-gray-200 rounded-[10px] mb-2 overflow-hidden">
            <button
              type="button"
              aria-expanded={open}
              onClick={() => setOpenRoles((prev) => ({ ...prev, [acc.role]: !open }))}
              className="w-full text-left bg-white px-3 py-2.5 flex justify-between items-center gap-3 hover:bg-gray-50"
            >
              <span className="flex items-center gap-2 min-w-0">
                <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap ${ROLE_BADGE[acc.role] || 'bg-gray-100 text-gray-700'}`}>
                  {title}
                </span>
                <span className="text-sm text-gray-800 whitespace-nowrap">{checkedFull} von {fullKeys.length}</span>
              </span>
              <span className="text-xs text-gray-500 truncate">{ROLE_BLURB[acc.role] || ''}</span>
            </button>
            {open && (
              <div className="px-3 pb-2.5 pt-1 border-t border-gray-100 bg-gray-50">
                <div className="flex flex-wrap gap-2 mb-2">
                  <button
                    type="button"
                    disabled={disabled}
                    onClick={() => setRoleComplete(acc)}
                    className={`text-[11px] px-2 py-1 rounded-lg border bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 ${complete ? 'border-indigo-200' : 'border-gray-300'}`}
                  >
                    Rolle komplett
                  </button>
                  <button
                    type="button"
                    disabled={disabled}
                    onClick={() => clearRole(acc)}
                    className="text-[11px] px-2 py-1 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Keine
                  </button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-0.5">
                  {acc.full_role.map((opt) => {
                    const chip = overlapLabel((rolesByKey.get(opt.key) ?? []).filter((r) => r !== acc.role))
                    return (
                      <label key={opt.key} className="flex items-start gap-2 text-[13px] cursor-pointer py-1 px-1 rounded-md hover:bg-gray-100">
                        <input
                          type="checkbox"
                          aria-label={opt.label}
                          checked={selected.has(opt.key)}
                          onChange={() => toggle(opt.key)}
                          disabled={disabled}
                          className="mt-0.5 h-4 w-4 rounded border-gray-300 text-indigo-600"
                        />
                        <span className="text-gray-700">
                          {opt.label}
                          {chip && (
                            <span className="ml-1.5 text-[11px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600 whitespace-nowrap">
                              {chip}
                            </span>
                          )}
                        </span>
                      </label>
                    )
                  })}
                </div>
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}
